import React from 'react';
import { Shield, ImageIcon } from 'lucide-react';
interface CoachCardProps {
  name: string;
  role: string;
  bio: string;
  imageUrl?: string;
  achievements?: string[];
  comingSoon?: boolean;
}
function ComingSoonImage({
  name
}: {
  name: string;
}) {
  return <div role="img" aria-label={`Photo of ${name} coming soon`} className="absolute inset-0 overflow-hidden" data-id="element-128">
      {/* Base */}
      <div className="absolute inset-0 bg-gradient-to-br from-ink via-charcoal to-ink" data-id="element-129" />
      {/* Decorative grid */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.08]" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-id="element-130">
        <defs data-id="element-131">
          <pattern id="coach-grid" width="32" height="32" patternUnits="userSpaceOnUse" data-id="element-132">
            <path d="M 32 0 L 0 0 0 32" fill="none" stroke="white" strokeWidth="1" data-id="element-133" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#coach-grid)" data-id="element-134" />
      </svg>
      {/* Red accent bar */}
      <div className="absolute top-0 right-0 h-full w-1/3 bg-storm-red/15 skew-x-12 translate-x-16" data-id="element-135" />
      <div className="absolute -top-10 -right-10 w-48 h-48 bg-storm-red/20 rounded-full blur-3xl" data-id="element-136" />
      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" data-id="element-137">
        <div className="w-16 h-16 rounded-2xl bg-storm-red/15 border border-storm-red/40 flex items-center justify-center mb-5" data-id="element-138">
          <ImageIcon className="w-8 h-8 text-storm-red" strokeWidth={2} aria-hidden="true" data-id="element-139" />
        </div>
        <p className="font-display text-xl md:text-2xl text-white uppercase tracking-wider leading-tight" data-id="element-140">
          Image
        </p>
        <p className="font-display text-xl md:text-2xl text-storm-red uppercase tracking-wider leading-tight" data-id="element-141">
          Coming Soon
        </p>
        <div className="mt-4 h-px w-16 bg-white/30" data-id="element-142" />
      </div>
    </div>;
}
export function CoachCard({
  name,
  role,
  bio,
  imageUrl,
  achievements,
  comingSoon
}: CoachCardProps) {
  // Render with a real photo OR the "Image Coming Soon" placeholder graphic.
  if (imageUrl || comingSoon) {
    return <div className="bg-paper rounded-2xl overflow-hidden shadow-soft border border-subtle-gray group" data-id="element-143">
        <div className="aspect-[4/5] overflow-hidden relative" data-id="element-144">
          {comingSoon ? <ComingSoonImage name={name} data-id="element-145" /> : <>
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent z-10" data-id="element-146" />
              <img src={imageUrl} alt={name} className="block w-full h-full object-cover filter grayscale group-hover:grayscale-0 transition-all duration-500 transform group-hover:scale-105" data-id="element-147" />
            </>}
          <div className="absolute bottom-0 left-0 p-6 z-20 w-full bg-gradient-to-t from-ink via-ink/70 to-transparent" data-id="element-148">
            <p className="text-storm-red font-bold uppercase tracking-wide text-sm mb-1" data-id="element-149">
              {role}
            </p>
            <h3 className="text-3xl text-white mb-0" data-id="element-150">{name}</h3>
          </div>
        </div>
        <div className="p-6" data-id="element-151">
          <p className="text-gray-warm leading-relaxed mb-0" data-id="element-152">{bio}</p>
          {achievements && achievements.length > 0 && <ul className="mt-4 flex flex-col gap-1.5 list-none p-0" data-id="element-153">
              {achievements.map((a, idx) => <li key={idx} className="text-sm text-storm-red font-bold uppercase tracking-wide" data-id="element-154">
                  • {a}
                </li>)}
            </ul>}
        </div>
      </div>;
  }
  // Neutral coach card — used when no real coach photo is available.
  // We intentionally avoid stock portraits of strangers misrepresenting real coaches.
  return <div className="bg-paper rounded-2xl overflow-hidden shadow-soft border border-subtle-gray group flex flex-col" data-id="element-155">
      <div className="relative bg-gradient-to-br from-ink to-charcoal px-6 py-10 flex flex-col items-center text-center overflow-hidden" data-id="element-156">
        <div className="absolute -top-12 -right-12 w-40 h-40 bg-storm-red/20 rounded-full blur-3xl pointer-events-none" data-id="element-157" />
        <div className="relative z-10 w-20 h-20 rounded-2xl bg-storm-red/15 border border-storm-red/40 flex items-center justify-center mb-5" data-id="element-158">
          <Shield className="w-9 h-9 text-storm-red" strokeWidth={2.25} data-id="element-159" />
        </div>
        <p className="relative z-10 text-storm-red font-bold uppercase tracking-wide text-xs mb-2" data-id="element-160">
          {role}
        </p>
        <h3 className="relative z-10 text-2xl text-white m-0 leading-tight" data-id="element-161">
          {name}
        </h3>
      </div>
      <div className="p-6 flex-1 flex flex-col" data-id="element-162">
        <p className="text-gray-warm leading-relaxed mb-0" data-id="element-163">{bio}</p>
        {achievements && achievements.length > 0 && <ul className="mt-4 flex flex-col gap-1.5 list-none p-0" data-id="element-164">
            {achievements.map((a, idx) => <li key={idx} className="text-xs text-storm-red font-bold uppercase tracking-wide" data-id="element-165">
                • {a}
              </li>)}
          </ul>}
      </div>
    </div>;
}